// rc117 — Reemplazo de ONU (equipo quemado/dañado) en un contrato de fibra.
// Baja la ONU actual en ByteOLT (+ retiro a almacén en ByteACS) y aprovisiona
// la nueva en ambos con el MISMO picker del alta: huérfanas en vivo, ya
// aprovisionadas sin cliente, o pre-registro si la OLT aún no la ve.
// El PPPoE se REGENERA con la serie nueva (user = pass = serie larga) y el
// secret viaja al router en un reconcile encolado — decisiones Alex 09-2026:
// baja primero / PPPoE de la serie nueva / botón en el editor unificado.
import { useEffect, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, RefreshCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { LabelCaptureOnu, fmtMac } from "@/components/LabelCaptureOnu";
import { genWifiPass } from "@/lib/wifiCard";
import {
  useOnusHuerfanas, usePlantillasOlt, usePerfilesOlt, useOltsByteolt,
  useEquipoAcs,
  type OnuHuerfana,
} from "@/features/integraciones/api";
import { useReemplazarOnu, type OnuReemplazoArgs, type OnuReemplazoResult } from "./api";
import type { ContratoDetail } from "./schemas";
import { PasswordRevealDialog } from "./PasswordRevealDialog";

/** rc109 — forma LARGA/hex de la serie (mismo helper del alta):
 *  "HWTCe677d3b7" → "48575443E677D3B7". */
function serialLargo(s: string): string {
  // rc120 — la CLI de la OLT puede imprimir la serie CON separador
  // (TP-Link autofind: "HWTC-E677D3B7") — se limpia antes de convertir,
  // si no el picker mostraba la forma cruda y el PPPoE no se pre-llenaba.
  const t = s.trim().replace(/[^0-9A-Za-z]/g, "");
  if (/^[0-9a-fA-F]{16}$/.test(t)) return t.toUpperCase();
  const m = /^([A-Za-z]{4})([0-9a-fA-F]{8})$/.exec(t);
  if (!m) return t || s.trim();
  const pfx = [...m[1]]
    .map((c) => c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0"))
    .join("");
  return pfx + m[2].toUpperCase();
}

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contrato: ContratoDetail;
  /** Se llama al confirmar las credenciales nuevas (cerrar y refrescar). */
  onDone?: () => void;
};

export function ReemplazarOnuDialog({ open, onOpenChange, contrato, onDone }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [result, setResult] = useState<OnuReemplazoResult | null>(null);

  // Picker (mismos estados del alta)
  const [onuSerialSel, setOnuSerialSel] = useState("");
  const [templateSel, setTemplateSel] = useState("");
  const [perfilSel, setPerfilSel] = useState("");
  const [preMode, setPreMode] = useState(false);
  const [preSerial, setPreSerial] = useState("");
  const [preOltId, setPreOltId] = useState("");
  // Módem · ACS
  const [acsSerial, setAcsSerial] = useState("");
  const [acsSerialQuery, setAcsSerialQuery] = useState("");
  // rc121 — clave WiFi opcional para el módem NUEVO (tarjeta QR imprimible)
  const [wifiPass, setWifiPass] = useState("");
  // rc122 — SSID personalizado (conservar la red del módem anterior)
  const [ssidCustom, setSsidCustom] = useState("");
  const [onuMac, setOnuMac] = useState("");
  const [showCaptura, setShowCaptura] = useState(false);
  const acsSerialDirty = useRef(false);

  const serialViejo = contrato.fiber_detalle?.onu_serial ?? "";

  const {
    data: byteoltData, isFetching: onusLoading, refetch: refetchOnus,
  } = useOnusHuerfanas(open);
  const byteoltEnabled = byteoltData?.enabled;
  const byteacsEnabled = byteoltData?.byteacs_enabled === true;
  const onusHuerfanas = byteoltData?.onus ?? [];
  const onusSinCliente = byteoltData?.sin_cliente ?? [];
  const onuSel =
    onusHuerfanas.find((o) => o.serial === onuSerialSel) ??
    onusSinCliente.find((o) => o.serial === onuSerialSel);
  const onuYaAprov = onusSinCliente.some((o) => o.serial === onuSerialSel);
  const plantillaOltId = preMode
    ? (preOltId || null)
    : !onuYaAprov ? onuSel?.olt_id ?? null : null;
  const { data: plantillas } = usePlantillasOlt(plantillaOltId);
  const { data: perfiles } = usePerfilesOlt(plantillaOltId);
  const { data: oltsData } = useOltsByteolt(open && byteoltEnabled === true && preMode);
  const preOlt = (oltsData ?? []).find((o) => o.id === preOltId);
  const { data: equipoAcs, isFetching: equipoAcsLoading } = useEquipoAcs(
    acsSerialQuery, open && byteacsEnabled,
  );

  // ONU elegida → sync serial ACS + MAC (si ByteOLT la conoce)
  useEffect(() => {
    setTemplateSel("");
    setPerfilSel("");
    if (!onuSerialSel) return;
    const largo = serialLargo(onuSerialSel);
    if (!acsSerialDirty.current) setAcsSerial(largo);
    const oSel =
      onusHuerfanas.find((o) => o.serial === onuSerialSel) ??
      onusSinCliente.find((o) => o.serial === onuSerialSel);
    if (oSel?.mac && !onuMac.trim()) setOnuMac(fmtMac(oSel.mac));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onuSerialSel]);

  // Pre-registro: el serial tecleado pre-llena el módem ACS
  useEffect(() => {
    if (!preMode) return;
    const largo = serialLargo(preSerial.trim());
    if (!/^[0-9A-F]{12,16}$/i.test(largo)) return;
    if (!acsSerialDirty.current) setAcsSerial(largo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSerial, preMode]);

  useEffect(() => {
    setTemplateSel("");
    setPerfilSel("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preOltId]);

  useEffect(() => {
    if (preMode) setOnuSerialSel("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preMode]);

  // Debounce consulta ACS
  useEffect(() => {
    const t = setTimeout(() => setAcsSerialQuery(acsSerial.trim().toUpperCase()), 600);
    return () => clearTimeout(t);
  }, [acsSerial]);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setError(null);
      setMotivo("");
      setResult(null);
      setOnuSerialSel("");
      setTemplateSel("");
      setPerfilSel("");
      setPreMode(false);
      setPreSerial("");
      setPreOltId("");
      setAcsSerial("");
      setAcsSerialQuery("");
      setWifiPass("");
      setSsidCustom("");
      setOnuMac("");
      setShowCaptura(false);
      acsSerialDirty.current = false;
    }
  }, [open]);

  const mut = useReemplazarOnu(contrato.id);

  function renderOnuRow(o: OnuHuerfana, aprovisionada = false) {
    const sel = o.serial === onuSerialSel;
    const esLaActual = serialLargo(o.serial) === serialLargo(serialViejo);
    const rxWarn = o.rx_dbm != null && o.rx_dbm < -25;
    return (
      <button
        type="button"
        key={o.serial}
        disabled={esLaActual}
        onClick={() => {
          setPreMode(false);
          setOnuSerialSel(sel ? "" : o.serial);
        }}
        className={cn(
          "flex w-full items-center gap-3 border-b px-3 py-2 text-left text-xs last:border-b-0",
          esLaActual
            ? "cursor-not-allowed opacity-45"
            : sel ? "bg-primary/10" : "hover:bg-primary/5",
        )}
      >
        <span
          className={cn(
            "relative h-3.5 w-3.5 flex-none rounded-full border-2",
            sel ? "border-primary" : "border-muted-foreground/40",
          )}
        >
          {sel && <span className="absolute inset-[2px] rounded-full bg-primary" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-[11.5px]">
            {serialLargo(o.serial)}
          </span>
          <span className="block truncate font-mono text-[10px] text-muted-foreground">
            {[
              esLaActual ? "ES LA ACTUAL" : null,
              serialLargo(o.serial) !== o.serial ? o.serial : null,
              o.vendor, o.descripcion,
              aprovisionada && o.vlan != null ? `VLAN ${o.vlan}` : null,
            ].filter(Boolean).join(" · ") || "—"}
          </span>
        </span>
        <span className="flex-none text-right font-mono text-[10.5px] text-muted-foreground">
          <span className="block">{o.olt ?? "?"}</span>
          <span className="block text-[9.5px]">PON {o.pon ?? "?"}</span>
        </span>
        <span
          className={cn(
            "w-16 flex-none text-right font-mono text-[10.5px]",
            rxWarn
              ? "text-amber-600 dark:text-amber-400"
              : "text-emerald-600 dark:text-emerald-400",
          )}
        >
          {o.rx_dbm != null ? `${o.rx_dbm} dBm` : "—"}
        </span>
      </button>
    );
  }

  async function submit() {
    setError(null);
    if (motivo.trim().length < 5) {
      setError("Escribe el motivo del reemplazo (mínimo 5 caracteres).");
      return;
    }
    let byteolt: OnuReemplazoArgs["byteolt"];
    if (preMode) {
      const pserial = serialLargo(preSerial.trim());
      if (!/^[0-9A-F]{12,16}$/i.test(pserial)) {
        setError("Escribe (o escanea) la serie de la ONU nueva a pre-registrar.");
        return;
      }
      if (!preOltId) {
        setError("Elige la OLT destino del pre-registro.");
        return;
      }
      if (preOlt?.vendor === "vsol" && !templateSel && (plantillas?.length ?? 0) > 0) {
        setError("Elige la plantilla de servicio (VLANs) para esa OLT.");
        return;
      }
      byteolt = {
        serial: pserial,
        pre_registro: true,
        olt_id: preOltId,
        ...(templateSel ? { template_id: templateSel } : {}),
        ...(perfilSel ? { line_profile: perfilSel } : {}),
      };
    } else {
      if (!onuSerialSel) {
        setError("Elige la ONU nueva (o usa el pre-registro si la OLT aún no la ve).");
        return;
      }
      if (!onuYaAprov && onuSel?.olt_vendor === "vsol" && !templateSel && (plantillas?.length ?? 0) > 0) {
        setError("Elige la plantilla de servicio (VLANs) para esta OLT.");
        return;
      }
      byteolt = onuYaAprov
        ? { serial: onuSerialSel, ya_aprovisionada: true }
        : {
            serial: onuSerialSel,
            ...(templateSel ? { template_id: templateSel } : {}),
            ...(perfilSel ? { line_profile: perfilSel } : {}),
          };
    }
    if (byteacsEnabled && wifiPass.trim() && wifiPass.trim().length < 8) {
      setError("La contraseña WiFi debe tener al menos 8 caracteres (o déjala vacía).");
      return;
    }
    try {
      const res = await mut.mutateAsync({
        motivo: motivo.trim(),
        byteolt,
        ...(byteacsEnabled && acsSerial.trim()
          ? {
              byteacs: {
                serial: acsSerial.trim().toUpperCase(),
                ...(wifiPass.trim().length >= 8 ? { wifi_pass: wifiPass.trim() } : {}),
                ...(ssidCustom.trim() ? { ssid: ssidCustom.trim() } : {}),
              },
            }
          : {}),
        ...(onuMac.trim() ? { onu_mac: onuMac.trim() } : {}),
      });
      setResult(res);
    } catch (e) {
      setError((e as ApiError).message ?? "No se pudo reemplazar la ONU");
    }
  }

  function handleDone() {
    setResult(null);
    onOpenChange(false);
    onDone?.();
  }

  const submitting = mut.isPending;

  return (
    <>
      <Dialog open={open && !result} onOpenChange={onOpenChange} size="lg">
        <DialogHeader>
          <DialogTitle>Reemplazar ONU — {contrato.codigo}</DialogTitle>
          <DialogDescription>
            Da de baja la ONU actual en ByteOLT{byteacsEnabled ? " y ByteACS" : ""} y
            aprovisiona la nueva en ambos. El PPPoE se regenera con la serie nueva.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          {byteoltEnabled === false ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              <strong>Integración ByteOLT desactivada.</strong> Actívala en
              Ajustes › Integraciones para reemplazar la ONU desde aquí.
            </div>
          ) : (
            <>
              {/* Resumen de lo que va a pasar */}
              <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                <AlertTriangle size={13} className="mt-0.5 flex-none" />
                <span>
                  Se da de baja <strong className="font-mono">{serialLargo(serialViejo) || "—"}</strong>{" "}
                  (deprovision en ByteOLT{byteacsEnabled ? " + retiro a almacén en ByteACS" : ""}),
                  se aprovisiona la nueva y el PPPoE del contrato pasa a la{" "}
                  <strong>serie nueva</strong> (el secret se actualiza en el router
                  con un job). El módem nuevo se autoconfigura por TR-069 en su
                  primer Inform.
                </span>
              </div>

              {/* Picker de ONU nueva (mismo del alta) */}
              <div className="mb-2 flex items-center justify-between">
                <Label className="mb-0">
                  ONU nueva{" "}
                  <span className="font-mono text-[10px] font-normal text-primary">
                    · en vivo desde ByteOLT
                  </span>
                </Label>
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => refetchOnus()}
                  disabled={onusLoading}
                >
                  <RefreshCw size={12} className={onusLoading ? "animate-spin" : ""} />
                </Button>
              </div>
              <div className="overflow-hidden rounded-md border">
                <div className="border-b bg-muted/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                  Por autorizar · {onusHuerfanas.length}
                </div>
                {onusLoading && onusHuerfanas.length === 0 && onusSinCliente.length === 0 ? (
                  <p className="p-4 text-center text-xs text-muted-foreground">
                    Consultando ONUs en ByteOLT…
                  </p>
                ) : onusHuerfanas.length === 0 ? (
                  <p className="p-3 text-center text-[11px] text-muted-foreground">
                    No hay ONUs por autorizar. Conecta la ONU nueva a la fibra y
                    refresca — aparece en cuanto la OLT la ve.
                  </p>
                ) : (
                  onusHuerfanas.map((o) => renderOnuRow(o))
                )}
                {onusSinCliente.length > 0 && (
                  <>
                    <div className="border-b border-t bg-muted/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                      Ya aprovisionadas · sin cliente · {onusSinCliente.length}
                    </div>
                    {onusSinCliente.map((o) => renderOnuRow(o, true))}
                  </>
                )}
              </div>

              {/* Pre-registro (la OLT aún no ve la ONU nueva) */}
              <button
                type="button"
                onClick={() => setPreMode((v) => !v)}
                className={cn(
                  "mt-2 w-full rounded-md border px-3 py-2 text-left text-xs font-medium",
                  preMode
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-primary/5",
                )}
              >
                {preMode ? "● " : "○ "}¿La OLT aún no ve la ONU nueva? Pre-registrar
                serial — se autoriza sola en cuanto se conecte
              </button>
              {preMode && (
                <div className="mt-2 rounded-md border border-primary/25 bg-primary/5 p-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Serie de la ONU (de la etiqueta)</Label>
                      <div className="flex gap-1.5">
                        <Input
                          value={preSerial}
                          onChange={(e) => setPreSerial(e.target.value.toUpperCase())}
                          placeholder="48575443E677FEB7"
                          className="font-mono"
                        />
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => setShowCaptura(true)}
                        >
                          Escanear
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>OLT destino</Label>
                      <Select
                        value={preOltId}
                        onChange={(e) => setPreOltId(e.target.value)}
                      >
                        <option value="">Selecciona la OLT…</option>
                        {(oltsData ?? [])
                          .filter((o) => !o.read_only)
                          .map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.nombre}{o.modelo ? ` · ${o.modelo}` : ""}
                            </option>
                          ))}
                      </Select>
                    </div>
                    {preOltId && (
                      <>
                        <div>
                          <Label>Plantilla de servicio (VLANs)</Label>
                          <Select
                            value={templateSel}
                            onChange={(e) => setTemplateSel(e.target.value)}
                          >
                            <option value="">
                              {preOlt?.vendor === "tplink"
                                ? "TP-Link: las VLANs viven en el perfil — elige solo el perfil"
                                : (plantillas?.length ?? 0) === 0
                                  ? "Sin plantillas en esta OLT (VLAN default)"
                                  : "Selecciona plantilla…"}
                            </option>
                            {(plantillas ?? []).map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} — datos {t.vlan_datos}
                                {t.vlan_aprov ? ` + aprov ${t.vlan_aprov}` : ""}
                              </option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <Label>Perfil ONU</Label>
                          <Select
                            value={perfilSel}
                            onChange={(e) => setPerfilSel(e.target.value)}
                          >
                            <option value="">auto — default de la OLT</option>
                            {(perfiles ?? []).map((p, i) => {
                              const name = p.name ?? p.nombre ?? "";
                              return name ? (
                                <option key={`${name}-${i}`} value={name}>{name}</option>
                              ) : null;
                            })}
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-[10.5px] leading-relaxed text-primary">
                    La baja de la ONU actual y el cambio de PPPoE se hacen de una vez;
                    el pre-registro queda <strong>esperando conexión</strong> en ByteOLT
                    y el sondeo autoriza la nueva en cuanto aparezca en{" "}
                    <strong>{preOlt?.nombre ?? "la OLT elegida"}</strong>.
                  </p>
                </div>
              )}

              {/* Plantilla/perfil para huérfana elegida */}
              {onuSel && !onuYaAprov && (
                <div className="mt-3 grid grid-cols-2 gap-3 rounded-md border bg-muted/20 p-3">
                  <div>
                    <Label>Plantilla de servicio (VLANs)</Label>
                    <Select
                      value={templateSel}
                      onChange={(e) => setTemplateSel(e.target.value)}
                    >
                      <option value="">
                        {onuSel?.olt_vendor === "tplink"
                          ? "TP-Link: las VLANs viven en el perfil — elige solo el perfil"
                          : (plantillas?.length ?? 0) === 0
                            ? "Sin plantillas en esta OLT (VLAN default)"
                            : "Selecciona plantilla…"}
                      </option>
                      {(plantillas ?? []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — datos {t.vlan_datos}
                          {t.vlan_aprov ? ` + aprov ${t.vlan_aprov}` : ""}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label>Perfil ONU</Label>
                    <Select
                      value={perfilSel}
                      onChange={(e) => setPerfilSel(e.target.value)}
                    >
                      <option value="">auto — default de la OLT</option>
                      {(perfiles ?? []).map((p, i) => {
                        const name = p.name ?? p.nombre ?? "";
                        return name ? (
                          <option key={`${name}-${i}`} value={name}>{name}</option>
                        ) : null;
                      })}
                    </Select>
                  </div>
                </div>
              )}
              {onuSel && onuYaAprov && (
                <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-[11px] leading-relaxed text-emerald-600 dark:text-emerald-400">
                  <strong className="font-mono">{serialLargo(onuSel.serial)}</strong>{" "}
                  ya está aprovisionada en{" "}
                  <strong>{onuSel.olt} · PON {onuSel.pon ?? "?"}</strong>
                  {onuSel.vlan != null ? <> (VLAN {onuSel.vlan})</> : null} — solo se
                  LIGA al contrato, sin tocar la OLT.
                </div>
              )}

              {/* Módem · ACS + MAC */}
              <div className="mt-3 grid grid-cols-2 gap-3">
                {byteacsEnabled && (
                  <div>
                    <Label className="flex items-center justify-between gap-2">
                      <span>Serial del módem (ACS)</span>
                      <button
                        type="button"
                        className="text-[10px] text-primary hover:underline"
                        onClick={() => setShowCaptura(true)}
                      >
                        Escanear etiqueta
                      </button>
                    </Label>
                    <Input
                      value={acsSerial}
                      onChange={(e) => {
                        acsSerialDirty.current = true;
                        setAcsSerial(e.target.value.toUpperCase());
                      }}
                      placeholder="Auto: la serie de la ONU nueva"
                      className="font-mono"
                    />
                    {acsSerialQuery.length >= 8 && (
                      <p className="mt-1 text-[10.5px] leading-relaxed">
                        {equipoAcsLoading ? (
                          <span className="text-muted-foreground">Consultando en ByteACS…</span>
                        ) : equipoAcs?.found === false ? (
                          <span className="text-primary">Serial nuevo en ByteACS.</span>
                        ) : equipoAcs?.found === true && equipoAcs.link?.client_nombre ? (
                          <span className="font-medium text-amber-600 dark:text-amber-400">
                            ⚠ Ya LIGADO a {equipoAcs.link.client_nombre}
                            {equipoAcs.link.contract_codigo ? ` (${equipoAcs.link.contract_codigo})` : ""} —
                            verifica antes de reusarlo.
                          </span>
                        ) : equipoAcs?.found === true && equipoAcs.lifecycle === "retired" ? (
                          <span className="text-emerald-600 dark:text-emerald-400">
                            ✓ En ALMACÉN — se reactiva y reconfigura solo.
                          </span>
                        ) : equipoAcs?.found === true ? (
                          <span className="text-emerald-600 dark:text-emerald-400">
                            ✓ Registrado y libre — se re-registra al reemplazar.
                          </span>
                        ) : null}
                      </p>
                    )}
                  </div>
                )}
                {byteacsEnabled && (
                  <div>
                    <Label>SSID personalizado (opcional)</Label>
                    <Input
                      value={ssidCustom}
                      onChange={(e) => setSsidCustom(e.target.value)}
                      placeholder="Vacío = BYTECOM_ + últimos 6 de la MAC"
                      maxLength={32}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      Conserva el nombre de la red del módem anterior — los
                      equipos del cliente reconectan solos (misma clave abajo).
                    </span>
                  </div>
                )}
                {byteacsEnabled && (
                  <div>
                    <Label>Contraseña WiFi (opcional)</Label>
                    <div className="flex gap-1.5">
                      <Input
                        value={wifiPass}
                        onChange={(e) => setWifiPass(e.target.value)}
                        placeholder="Vacía = clave de fábrica"
                        className="font-mono"
                      />
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setWifiPass(genWifiPass())}
                      >
                        Generar
                      </Button>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      ByteACS la aplica al módem nuevo en su primer Inform —
                      lista para la tarjeta QR.
                    </span>
                  </div>
                )}
                <div>
                  <Label>MAC del módem nuevo</Label>
                  <Input
                    value={onuMac}
                    onChange={(e) => setOnuMac(e.target.value.toUpperCase())}
                    placeholder="14:65:6A:95:AC:F6"
                    className="font-mono"
                  />
                  <span className="text-[10px] text-muted-foreground">
                    Se llena sola al escanear o si ByteOLT la conoce. SSID =
                    BYTECOM_ + últimos 6. Vacía = se limpia la del equipo viejo.
                  </span>
                </div>
              </div>

              {/* Motivo */}
              <div className="mt-3">
                <Label>Motivo del reemplazo *</Label>
                <Textarea
                  rows={2}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ej. ONU quemada por descarga eléctrica"
                />
              </div>

              {error && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button" size="sm" variant="destructive"
            onClick={submit}
            disabled={submitting || byteoltEnabled === false}
          >
            {submitting ? "Reemplazando…" : "Reemplazar ONU"}
          </Button>
        </DialogFooter>
      </Dialog>

      {showCaptura && (
        <LabelCaptureOnu
          onClose={() => setShowCaptura(false)}
          onUse={(d) => {
            if (d.serial) {
              if (preMode) {
                setPreSerial(d.serial);
              } else {
                acsSerialDirty.current = true;
                setAcsSerial(d.serial);
              }
            }
            if (d.mac) setOnuMac(fmtMac(d.mac));
          }}
        />
      )}

      {result && (
        <PasswordRevealDialog
          open={true}
          onConfirm={handleDone}
          pppoeUser={result.pppoe_user}
          password={result.pppoe_password_plain}
          title="ONU reemplazada — PPPoE nuevo"
          subtitle={[
            `El PPPoE del contrato ${contrato.codigo} ahora es la serie nueva; el router se actualiza con un job.`,
            result.pre_registro_esperando
              ? "La ONU nueva quedó PRE-registrada en ByteOLT — se autoriza sola al conectarse."
              : null,
            ...(result.avisos ?? []),
          ].filter(Boolean).join(" ")}
        />
      )}
    </>
  );
}
